import {
	fireClickyGoal, fireGoogleEcommerce
} from 'helpers/analyticsHelpers';
import { ga4Purchase } from 'helpers/dataLayerHelpers';
import {
	find,
	forEach,
	get,
	includes,
	intersection,
	isEmpty,
	keys,
	merge, noop,
	reject,
	values,
} from 'lodash';

import { SIX_MONTH_CADENCE_OPTION } from '../apps/medicationCheckoutV2/src/App.helpers';

export const dollarFormat = (cents) => {
	return `$${cents/100}`
}

const DEFAULT_ERROR = 'It looks like there was an error processing your payment. Please try again, or contact care@apostrophe.com for assistance.'
const formatDiscounts = (promos) => {
	let discount = {
		rx_credit: 0,
		amount: 0,
		coupon_code: null,
	}

	Object.values(promos).forEach(promo => {
		discount.amount += promo.applied_credit_cents
		if (promo.code == 'rx_credit') {
			discount.rx_credit += promo.applied_credit_cents
		} else {
			discount.coupon_code = promo.code
		}
	})
	return discount
}

const getActivePromo = (cartPromos) => {
	return find(Object.values(cartPromos),
		(promoCode) => promoCode != 'rx_credit')
}

// props passed straight from componentProps
// transaction type is braintree or stripe
const postCheckout = async (props, transactionType) => {
	let data = props.cart;
	if (transactionType) {
		data.paymentMethod = transactionType === 'paypal' ? 'braintree' : 'stripe';
	}
	data.paymentMethod = data.paymentMethod || props.paymentInfo && props.paymentInfo.defaultPaymentMethod;
	data.subscriptionID = getSubscriptionId(props.cart.products);
	data.promo_code = getPromoCode(props.cart.promos);
	const url = determineCheckoutUrl(props);
	data.discount = formatDiscounts(props.cart.promos);
	data.metadata = {
		transactionType: transactionType || data.paymentMethod,
		userAgent: window && window.navigator && window.navigator.userAgent,
	};

	if (props.isFollowupv2) {
		data.is_purchase = true;
	}

	if (props.isPaymentCadence) {
		data.is_payment_cadence_purchase = true;
	}

	if (props.isArContM2Submit) {
		data.isArContM2Submit = true
	}

	if (props.is_wrinkle_sub) {
		data.is_wrinkle_sub = true
	}

	if (props.consultationId) {
		data.consultation_id = props.consultationId
	}

	if (props.cadencePreference) {
		data.cadence_preference = props.cadencePreference
	}

	if (props.cartBypassProductsToSendToAjaxPrepayEndpoint) {
		data.products_bypass = props.cartBypassProductsToSendToAjaxPrepayEndpoint;
	}

	// only relevant for checkout page ARFollowupCheckout, used for patients who submit a f/u with an active AR
	if (!isEmpty(props.newShipmentDates)) {
		let shipments = {}
		// simplify new shipment dates object to { "Azelaic Acid": 16880003 }
		forEach(props.newShipmentDates,
			({
				drugname, date,
			}) => shipments[drugname] = date.unix());
		data.userSelectedFuturePaymentDates = shipments;
	}
	if (data.RXKit && data.bundlePrice && data.bundleDiscountPrice) {
		data.discount.checkout_rx_kit = data.bundlePrice - data.bundleDiscountPrice;
		data.discount.amount += data.discount.checkout_rx_kit
	}
	// The preCheckoutHook allows for validation of other data on the page,
	// and formatting data to send in addition to above checkout Information
	// with the checkout request. If it fails, do not continue.
	if (props.preCheckoutHook) {
		let errors, additionalData;
		let res = await props.preCheckoutHook(props);
		if (res.errors) {
			console.log(errors);
			return { error: errors }
		} else if (res.data) {
			data = merge(data,
				res.data)
		}
	}

	let paid = {};
	try {
		const Response = await fetch(url,
			{
				method: 'POST',
				credentials: 'include',
				headers: {
					'X-CSRFToken': props.CSRFToken,
				},
				body: JSON.stringify(data),
			});
		paid = await Response.json();
	} catch(err) {
		console.log(err);
		return {
			success: false,
			errorMessage: DEFAULT_ERROR,
		}
	}

	if (!isEmpty(paid.success)) {
		paid.response = paid.response || {};
		const reportableProducts = props.cart && props.cart.products ? props.cart.products : [];
		const isSyncPayment = props.isSync || props.is_sync;
		try {
			await fireClickyGoal(paid.response);
			await fireGoogleEcommerce(paid.response,
				reportableProducts,
				props.patient);
			await ga4Purchase(paid.response,
				reportableProducts,
				props.patient,
				props.isFollowup,
				isSyncPayment,
				!!props.cartBypassProductsToSendToAjaxPrepayEndpoint,
			);
		}
		catch (err) {
			console.error('Analytics goal tracking failed')
			console.error(err)
		}
		return {
			success: true,
			response: paid.response,
			nextStep: paid.response.nextStep || determineNextUrl(props),
			errorMessage: undefined,
			successMessage: determineSuccessMessage(props),
		}
	} else {
		return {
			success: false,
			errorMessage: paid.error || DEFAULT_ERROR,
		}
	}
};

const getProductInfo = (productId, props) => {
	const descriptors = {
		LATISSE: 'Latisse',
		// depricated 02.11.20, revert to default latisse
		LATISSE_STARTER: 'Latisse starter kit',
		MPB: 'Finasteride',
		WRINKLE: 'Tretinoin Formula',
	}

	if (productId == props.purchaseableProduct) {
		return { descriptor_short: descriptors[productId] }
	}

	if (!props.productOptions && props.cart && props.cart.products && keys(props.cart.products).length) {
		return { descriptor_short: descriptors[keys(props.cart.products)[0]] }
	}

	let product = find(props.productOptions,
		['id', productId]);

	// Check for OTCs
	if (!product && props.otcProducts) {
		props.otcProducts.forEach((otc_product) => {
			if (otc_product.id === productId) {
				product = otc_product
			}
		})
	}

	return product;
};

const createCartLineItems = (props, isCadenceCheckout) => {
	const cart = props.cart;
	const lineItems = [];
	const pre_purchase = window && window.Yoderm && window.Yoderm.data && window.Yoderm.data.prePurchase;
	let cadenceSelected = 1; // At the end, we multiply the cost of all items by this value.
	let gift_card_visit = false;
	let realCost = 0;
	let buyMoreSavings = 0;
	let totalRXCredit = 0;
	if (props.patient && props.patient.gift_cards && props.patient.gift_cards.length) {
		if (cart.visitFeeCents === 0) {
			gift_card_visit = true
		}
	}
	if (props.followup_cents == 0 || (cart && cart.cartTotalCents == 0) && !cart.promos) {
		lineItems.push({
			type: 'free_visit',
			description: !props.isFollowup ? 'FREE visit fee' : 'FREE follow-up visit fee',
			cost: <p style={{ margin: 0 }}><span style={{
				textDecoration: 'line-through',
				color: '#999999',
			}}>$20</span> <span style={{ color: '#006368' }}>$0</span></p>,
			deletable: false,
			conditionsTreated: cart.conditionsTreated,
			isFollowup: !!props.isFollowup,
		});
	}
	if ((window && window.location && !window.location.href.includes('order') && gift_card_visit)) {
		lineItems.push({
			type: 'visit_fee',
			description: !props.isFollowup ? 'Visit fee' : 'Follow-up visit fee',
			cost: 'FREE',
			deletable: false,
			conditionsTreated: cart.conditionsTreated,
		});
	} else if (cart.visitFeeCents > 0) {
		if (!pre_purchase) {
			lineItems.push({
				type: 'visit_fee',
				description: !props.isFollowup ? 'Visit fee' : 'Follow-up visit fee',
				cost: dollarFormat(cart.visitFeeCents),
				deletable: false,
				conditionsTreated: cart.conditionsTreated,
			})
		}
	}
	Object.keys(cart.products).map((productId, index) => {
		const product = getProductInfo(productId,
			props);
		const option = cart.products[productId];
		const orderCadence = option.order_cadence;
		const autorefill = get(option,
			'autorefill',
			{});
		if (product) {
			let displayText = (product.descriptor_short || product.name);
			// Use medication aliases for cadence checkout
			if (displayText && isCadenceCheckout) {
				if (displayText.toLowerCase().includes('spironolactone') && !product.compounded) {
					displayText = 'Spironolactone oral tablet'
				} else if (displayText.toLowerCase().includes('screen spf')) {
					// Keep display text as is for 'Screen.
					noop();
				} else {
					displayText = 'Custom topical treatment';
				}
			}

			const newItem = {
				type: 'product',
				description: isCadenceCheckout
					? displayText + (orderCadence ? ' (2)' : '')
					: displayText,
				cost: dollarFormat(option.price_cents * (orderCadence || 1)),
				deleteable: false,
				autorefill: option.autorefill || {},
				addableCost: option.price_cents * (orderCadence || 1),
			};

			if (window && window.Yoderm && window.Yoderm.data && Yoderm.data.product_options) {
				Yoderm.data.product_options.forEach(outterOption => {
					if (outterOption.id === productId) {
						if (outterOption.options && outterOption.options.length > 1) {
							// #TODO unknown reason why, but months appears to be intentionally an optional field, previously missing some options
							// Multiple pricing options, the "real cost" is the retail amount which is calculated by amount based on a 1 month supply (max price) or without the AR discount
							let option_retailCost = 0; // OLDER LOGIC
							let singleMonthRetailPrice = 0; // Retail cost based on purchasing a 1-month supply
							let ar_retailCost = 0; // Auto-Refill retail cost
							let multiMonthRetailPrice = 0;
							// determine the best option for patients to save the most money. 2 mo or 3mo
							const maxOption = Math.max(...outterOption.options.map(option => option.months))
							outterOption.options.forEach(innerOption => {
								if (!innerOption.months) {
									// Previously, this code appears to work by assuming the months attribute is missing from one of the pricing options
									// Oral med has 2 pricing options (innerOption): 3-month supply with the months attribute, and a 1-month supply without a months attribute
									// Determine the retail cost based on the single month supply if purchased every month as a 1-month supply
									// Or just show the 1-month supply if that is the option selected
									if (innerOption.price_cents === option.price_cents) {
										option_retailCost = option.price_cents * (option.order_cadence || 1);
									} else if (option.months) {
										option_retailCost = innerOption.price_cents * option.months * (option.order_cadence || 1);
									} else {
										option_retailCost = innerOption.price_cents * (option.order_cadence || 1);
									}
								} else {
									if (innerOption.months === 1) {
										// Calculate retail cost based on single month price
										singleMonthRetailPrice = innerOption.price_cents * option.months * (option.order_cadence || 1);
									} // only resolve for 2 months if 3 mo isn't an option
									if ((innerOption.months === 3 || innerOption.months === 2) && maxOption === innerOption.months) {
										multiMonthRetailPrice = innerOption.price_cents * (option.order_cadence || 1);
									}
									if (innerOption.ar_discount_cents) {
										// Auto Refill cost for orals (spiro) already includes ar discount in retail cost (always show this cost)
										ar_retailCost = option.price_cents * (option.order_cadence || 1);
									}
								}
							})
							if (option_retailCost) {
								realCost += option_retailCost;
							} else if (ar_retailCost) {
								realCost += ar_retailCost;
							} else if (singleMonthRetailPrice) {
								realCost += singleMonthRetailPrice;
								// calculate savings if patient were to choose multi month option
								// for 1mo selection singleMonthRetailPrice is cheaper than multiMonthRetailPrice so buy more savings would not be calculated
								if (singleMonthRetailPrice - multiMonthRetailPrice > 0) {
									buyMoreSavings += (singleMonthRetailPrice - multiMonthRetailPrice);
								}
							}
						} else {
							realCost += option.price_cents * (option.order_cadence || 1);
						}
					}
				})
			}

			// Handle real cost for OTC only checkout
			if (window && window.Yoderm && window.Yoderm.data && Yoderm.data.otc_product_options && window.Yoderm.data.otcCheckout) {
				Yoderm.data.otc_product_options.forEach(outterOption => {
					if (outterOption.id === productId) {
						if (outterOption.options && outterOption.options.length > 1) {
							outterOption.options.forEach(innerOption => {
								if (!innerOption.months) {
									if (innerOption.price_cents === option.price_cents) {
										realCost += option.price_cents * (option.order_cadence || 1);
									} else {
										realCost += innerOption.price_cents * option.months * (option.order_cadence || 1);
									}
								}
							})
						} else {
							realCost += option.price_cents * (option.order_cadence || 1);
						}
					}
				})
			}

			// If this isCadenceCheckout, do not show the AR prices, so that users can see their savings.
			if (!isEmpty(autorefill) && autorefill.selected && !isCadenceCheckout) {
				newItem.cost = dollarFormat(autorefill.price_cents * (orderCadence || 1));
				newItem.addableCost = autorefill.price_cents * (orderCadence || 1);
			}

			lineItems.push(newItem);
		}
	});
	if (cart.product_pre_purchase && !Object.keys(cart.products).length) {
		cart.product_pre_purchase.map(product => {
			const newItem = {
				type: 'product',
				description: product.name,
				cost: dollarFormat(product.price_cents),
				deleteable: false,
				autorefill: false,
				addableCost: product.price_cents,
			};
			lineItems.push(newItem);
		});
	}

	Object.values(cart.promos).map(promo => {
		if (pre_purchase && (promo.applied_credit_cents === 0 || promo.description === 'Prescription credit')) {
			// Pre-purchase rx flow:
			//  + skip adding promo line item of $0 discount
			//    #TODO do we always want to hide the promo credit line item if the applied_credit_cents == 0?
			//    -> edge case to consider: if a promo code is applying a rx credit after purchase (but not a discount now) we might want to show it...
			//  + skip showing prescription credit (since visit is not shown)
		} else {
			totalRXCredit += promo.applied_credit_cents;
			lineItems.push({
				type: 'promo',
				description: promo.description,
				cost: `- ${dollarFormat(promo.applied_credit_cents)}`,
				discount: `${dollarFormat(promo.applied_credit_cents)}`,
				deletable: promo.deletable,
				isFollowup: !!props.isFollowup,
			})
		}
	});
	// Show kit discount instead of prescription savings if applicable (both meds)
	if (cart.bundlePrice && cart.bundleDiscountPrice) {
		let topicalCount = 0;
		let oralCount = 0;
		cart.purchaseable_products.forEach(product => {
			if (product.medication_type === 'topical' && cart.products[product.id]) {
				topicalCount += 1;
			} else if (product.medication_type === 'oral' && cart.products[product.id]) {
				oralCount += 1;
			}
		});
		if (topicalCount === 1 && oralCount === 1) {
			if (cart.bundlePrice - cart.bundleDiscountPrice > 0) {
				lineItems.push({
					type: 'kit_savings',
					description: 'Acne kit discount',
					cost: `-${dollarFormat(cart.bundlePrice - cart.bundleDiscountPrice)}`,
					deletable: false,
				});
			}
		}
	} else {
		if (realCost > 0 && (realCost - cart.cartTotalCents - totalRXCredit) > 0) {

			let prescription_savings = realCost - cart.cartTotalCents - totalRXCredit;
			// if a buy more savings exist (ie savings from getting 3mo/2mo supply over 1mo) do not include it in the prescription savings so that it's not accounted for twice.
			if (buyMoreSavings > 0) {
				prescription_savings -= buyMoreSavings;
			}

			if (prescription_savings > 0) {
				lineItems.push({
					type: 'prescription_savings',
					description: 'Auto-refill savings',
					cost: `-${dollarFormat(prescription_savings)}`,
					deletable: false,
				});
			}
		}
		if (buyMoreSavings > 0) {
			lineItems.push({
				type: 'buy_more_savings',
				description: 'Buy more & save discount',
				cost: `-${dollarFormat(buyMoreSavings)}`,
				deletable: false,
			});
		}
	}
	if (!isEmpty(cart.products)) {
		lineItems.push({
			type: 'shipping',
			description: 'FREE Shipping',
			cost: '$0',
			deleteable: false,
		})
	}
	lineItems.push({
		type: 'total',
		description: 'Total',
		cost: dollarFormat(cart.cartTotalCents),
		deletable: false,
		realCost: dollarFormat(realCost),
	});

	// Multiply line item price values by cadenceSelected
	lineItems.forEach(item => {
		if (item.type === 'product') {
			item.cost = `$${(item.cost.split('$')[1])}`;
		} else if (item.type === 'prescription_savings') {
			item.cost = `$${(item.cost.split('$')[1])}`;
		} else if (item.type === 'total') {
			item.cost = `$${(item.cost.split('$')[1])}`;
			item.realCost = `$${(item.realCost.split('$')[1])}`;
		}
	})

	return lineItems
};

// This creates line items to recreate a receipt from a persisted productOrder.
const createOrderLineItems = (order) => {
	let lineItems = [];
	const items = order.items || [];
	const otcItems = order.otc_items || [];
	const discount = order.discount || { amount: 0 };
	items.map(item => {
		lineItems.push({
			type: 'product',
			description: item.drugname,
			cost: dollarFormat(item.price_cents),
			deleteable: false,
		});
	});
	otcItems.map(item => {
		lineItems.push({
			type: 'product',
			description: item.name,
			cost: dollarFormat(item.price_cents),
			deleteable: false,
		});
	});
	if (order.productType && order.productType !== 'SKIN') {
		lineItems.push({
			type: 'product',
			description: order.productType.toLowerCase(),
			cost: dollarFormat(order.orderTotal + discount.amount),
			deleteable: false,
		});
	}
	if (order.rx_credit_purchased) {
		lineItems.push({
			type: 'product',
			description: `Apostrophe RX Credits ($${order.rx_credit_received / 100})`,
			cost: `$${order.rx_credit_purchased / 100}`,
			deleteable: false,
		});
	}
	if (discount.amount > 0) {
		const rx_credit = discount.rx_credit || 0;
		let discount_subtotal_counted = 0; // running subtotal of remaining cents accounted for
		if (rx_credit) {
			lineItems.push({
				type: 'promo',
				description: 'Prescription Credit Used',
				cost: `- ${dollarFormat(rx_credit)}`,
			})
			discount_subtotal_counted += rx_credit;
		}
		const bundle_discount = discount.bundle_discount || 0;
		if (bundle_discount > 0) {
			lineItems.push({
				type: 'promo',
				description: order.isRXKit ? 'Rx Kit Discount' : 'Bundle Discount',
				cost: `- ${dollarFormat(bundle_discount)}`,
			})
			discount_subtotal_counted += bundle_discount;
		}
		if (discount.amount - discount_subtotal_counted > 0) {
			lineItems.push({
				type: 'promo',
				description: discount.coupon_code || 'Discount',
				cost: `- ${dollarFormat(discount.amount - discount_subtotal_counted)}`,
			})
		}
	}
	if (order.rx_credit_purchased) {
		lineItems.push({
			type: 'visit',
			description: 'Next Visit',
			cost: 'FREE',
			deleteable: false,
		});
	}
	lineItems.push({
		type: 'shipping',
		description: 'Shipping',
		cost: 'FREE',
		deleteable: false,
	});
	lineItems.push({
		type: 'total',
		description: 'Total',
		cost: dollarFormat(order.rx_credit_purchased ? order.orderTotal + order.rx_credit_purchased : order.orderTotal),
	});
	return lineItems;
};

const getSubscriptionId = (products) => {
	const productTypes = keys(products)
	if (productTypes.length == 1 && includes(['WRINKLE', 'LATISSE', 'MPB', 'LATISSE_STARTER'],
		productTypes[0])) {
		return products[productTypes[0]].plan_id
	}
	return null
}

const getPromoCode = (promos) => {
	const promoKeys = keys(promos)
	if (promoKeys.length > 0) {
		const nonCreditPromos = reject(promoKeys,
			(key) => key == 'PRODUCT_CREDIT')
		if (nonCreditPromos.length == 1) {
			return nonCreditPromos[0]
		}
	}
}

const stripeSave = async (token, csrfToken, existingPayments=false) => {
	const stripeUrl = '/ajax/patient/account/stripe_checkout/';
	let data = { token }
	if (existingPayments) {
		data.existing_payments = true
	}
	const Response = await fetch(stripeUrl,
		{
			method: 'POST',
			credentials: 'include',
			headers: {
				'X-CSRFToken': csrfToken,
			},
			body: JSON.stringify(data),
		})
	return await Response.json()
}

const stripeTokenSave = async (props) => {
	let token = props.token
	console.log(token)
	if (!token) {
		const response = await props.stripe.createToken({
			type: 'card',
			name: getName(props.patient),
		})
		token = response.token
	}

	if (token) {
		return await stripeSave(token,
			props.CSRFToken,
			true)
	} else {
		return { error: true }
	}

}

const braintreeSave = async (data, csrfToken) => {
	const braintreeUrl = '/ajax/patient/account/braintree_billing/'
	const Response = await fetch(braintreeUrl,
		{
			method: 'POST',
			credentials: 'include',
			headers: {
				'X-CSRFToken': csrfToken,
			},
			body: JSON.stringify(data),
		})

	return await Response.json()
}

const getName = (patient) => {
	const firstName = (patient.firstname || '').toUpperCase()
	const lastName = (patient.lastname || '').toUpperCase()
	return `${firstName} ${lastName}`
}

const createTokenAndPostCheckout = async (props) => {
	const saved = await stripeTokenSave(props)
	if (!isEmpty(saved.success)) {
		let data = merge({ paymentMethod: 'stripe' },
			props)
		return await postCheckout(data,
			'stripe')
	} else {
		return {
			success: false,
			errorMessage: DEFAULT_ERROR,
		}
	}
};

const urls = {
	VISIT: {
		checkout: '/ajax/patient/account/pay/visit_only/',
		next: '/yd/register/shipping/?type=VISIT',
		successMessage: 'Success! You\'re one step closer to your treatment plan.',
	},
	OTC: {
		checkout: '/ajax/patient/otc_checkout/',
		next: '/yd/register/order-otcs/success/',
		successMessage: 'Success! You\'ll receive your product shortly!',
	},
	PREPURCHASE: {
		checkout: '/ajax/patient/account/pay/product_pre_purchase/',
		next: '/yd/register/shipping/?type=VISIT',
		successMessage: 'Success! You\'re one step closer to your treatment plan.',
	},
	LATISSE: {
		next: '/yd/register/shipping/latisse/?type=LASH',
		checkout: {
			0: '/ajax/patient/checkout/single_product/',
			1: '/ajax/patient/account/create_subscription/',
			2: '/ajax/patient/account/create_subscription/',
		},
		successMessage: 'Great! Thanks for your order.',
	},
	LATISSE_STARTER: {
		next: '/yd/register/shipping/latisse/?type=LASH',
		checkout: {
			0: '/ajax/patient/checkout/single_product/',
		},
		successMessage: 'Great! Thanks for your order.',
	},
	WRINKLE: {
		next: '/yd/register/shipping/tretinoin-plus/?type=FILI',
		checkout: {
			0: '/ajax/patient/checkout/single_product/',
			3: '/ajax/patient/account/create_subscription/',
		},
		successMessage: 'Wonderful! Thanks for your order.',
	},
	MPB: {
		checkout: '/ajax/patient/account/mpb_checkout/',
		next: '/yd/register/shipping/mpb/?type=HAIR',
		successMessage: 'Great! Thanks for your order.',
	},
	MEDS: {
		checkout: '/ajax/patient/medication_checkout/',
		next: '/yd/patient/order/success/?type=SKIN',
		successMessage: undefined, // no success message - we are going to a success page.
	},
	MEDS_V2: {
		checkout: '/ajax/patient/medication_checkout/',
		next: '/yd/patient/order_v2/success/?type=SKIN',
		successMessage: undefined, // no success message - we are going to a success page.
	},
	FOLLOWUP: {
		checkout: '/ajax/patient/confirm/followup/',
		next: '/yd/patient/dashboard/',
		successMessage: 'Thanks for submitting your follow-up! Your provider will get back to you soon.',
	},
	FOLLOWUP_V2: { // updated follow up that only includes payment as final step
		checkout: '/ajax/patient/confirm/followup/',
		next: '/yd/patient/medical_v2/confirm-review/',
		successMessage: 'Thanks for submitting your follow-up! Your provider will get back to you soon.',
	},
	SYNC_FOLLOWUP: {
		checkout: '/ajax/patient/confirm/followup/',
		next: '/yd/patient/appointments/schedule/',
		successMessage: 'Last step! Just schedule a brief video appointment with your provider.',
	},
};

function setBodyBusy(set=true) {
	if (set) {
		document.getElementsByTagName('body')[0].classList.add('busy')
	} else {
		document.getElementsByTagName('body')[0].classList.remove('busy')
	}
}

const determineSuccessMessage = (props) => {
	const products = props.cart && props.cart.products && keys(props.cart.products)
	if (props.isFollowup) {
		// TODO: remove once ar_continuation is completed
		if (props.isFollowupv2) {
			return urls.FOLLOWUP_V2.successMessage
		}
		if (props.isPaymentCadence) {
			return urls.MEDS_V2.successMessage
		}
		if (props.isSync) {
			return urls.SYNC_FOLLOWUP.successMessage
		}
		return urls.FOLLOWUP.successMessage
	} else if (isEmpty(products)) {
		return urls.VISIT.successMessage
	} else if (products.length === 1 && includes(['WRINKLE', 'LATISSE', 'MPB'],
		products[0])) {
		return urls[products[0]].successMessage
	} else {
		return urls.MEDS.successMessage
	}
};

const determineCheckoutUrl = (props) => {
	if (props.checkoutUrl) { return props.checkoutUrl }
	if (props.otcCheckout) { return urls.OTC.checkout }
	const products = props.cart && props.cart.products && keys(props.cart.products) || [];
	const productPrePurchase = props.cart && props.cart.product_pre_purchase || [];
	if (props.isFollowup) {
		// TODO: remove once ar_continuation is completed
		if (props.isFollowupv2) {
			return urls.FOLLOWUP_V2.checkout
		}
		if (props.isPaymentCadence) {
			return urls.MEDS_V2.checkout
		}
		if (props.isSync) {
			return urls.SYNC_FOLLOWUP.checkout
		}
		return urls.FOLLOWUP.checkout
	} else if (!isEmpty(productPrePurchase)) {
		return urls.PREPURCHASE.checkout
	} else if (isEmpty(products) && (props.cart.visitFeeCents || (props.patient && props.patient.registrationStep === 'PAYMENT'))) {
		return urls.VISIT.checkout
		// I dont think this logic is quite right... what if there is a free visit because of a promo??
	} else if (products.length > 1 && !isEmpty(intersection(products,
		['WRINKLE', 'LATISSE', 'MPB', 'LATISSE_STARTER']))) {
		console.error('Patient attempting to check out with subscription AND other products. Not allowed')
		return urls.MEDS.checkout
	} else if (products.length === 1 && products[0] === 'MPB') {
		return urls.MPB.checkout
	} else if (products.length === 1 && includes(['WRINKLE', 'LATISSE', 'LATISSE_STARTER'],
		products[0])) {
		const freq = values(props.cart.products)[0].frequency
		return urls[products[0]].checkout[freq]
	} else {
		return urls.MEDS.checkout
	}
};

const determineNextUrl = (props) => {
	if (props.isPaymentCadence) {
		return urls.MEDS_V2.next
	}
	if (props.nextStepUrl) { return props.nextStepUrl }

	const products = props.cart && props.cart.products && keys(props.cart.products) || [];
	if (props.isFollowup) {
		// TODO: remove once ar_continuation is completed
		if (props.isFollowupv2) {
			console.log('redirect! ')
			return urls.FOLLOWUP_V2.next
		}
		if (props.isSync) {
			return urls.SYNC_FOLLOWUP.next
		}
		return urls.FOLLOWUP.next
	} else if (isEmpty(products)) {
		return urls.VISIT.next
	} else if (products.length === 1 && includes(['WRINKLE', 'LATISSE', 'MPB', 'LATISSE_STARTER'],
		products[0])) {
		return urls[products[0]].next
	} else {
		return urls.MEDS.next
	}
};

const checkIfCartContainsAutoRefills = (products) => {
	if (isEmpty(products)) {
		return false;
	}
	let hasAutoRefills = false;
	values(products).forEach(({ autorefill }) => {
		if (hasAutoRefills) {
			return;
		}
		if (autorefill && !isEmpty(autorefill) && autorefill.selected) {
			hasAutoRefills = true;
		}
	});
	return hasAutoRefills;
}

const checkIfCartContainsSubscription = (products) => {
	if (isEmpty(products)) {
		return false;
	}
	let hasSubscription = false;
	values(products).forEach(({
		frequency, selected,
	}) => {
		if (hasSubscription) {
			return;
		}
		if (frequency && selected) {
			hasSubscription = true;
		}
	});
	return hasSubscription;
}

const getRecurringCostFromCart = (products, cadenceOption) => {
	if (isEmpty(products)) {
		return '';
	}
	let recurringCost = 0;
	values(products).forEach(({
		autorefill, frequency, selected, price_cents,
	}) => {
		if (autorefill && !isEmpty(autorefill) && autorefill.selected) {
			recurringCost += autorefill.price_cents;
		} else if (frequency && price_cents && selected) {
			recurringCost += price_cents;
		}
	});

	if (cadenceOption && cadenceOption === SIX_MONTH_CADENCE_OPTION) {
		recurringCost *= 2
	}

	if (recurringCost > 0) {
		return `$${recurringCost / 100}`;
	}
	return '';
}

const getRecurringFrequencyFromCart = (products, cadenceOption) => {
	if (isEmpty(products)) {
		return '';
	}
	let recurringFrequency = '';
	values(products).forEach(({
		autorefill, frequency, selected,
	}) => {
		if (autorefill && !isEmpty(autorefill) && autorefill.selected) {
			if (cadenceOption && cadenceOption === SIX_MONTH_CADENCE_OPTION) {
				recurringFrequency = 'every 6 months';
			} else {
				recurringFrequency = 'every 3 months';
			}
		} else if (frequency && selected) {
			if (frequency === 1) {
				recurringFrequency = 'every month';
			} else {
				recurringFrequency = `every ${frequency} months`;
			}
		}
	});
	return recurringFrequency;
}


export {
	braintreeSave,
	checkIfCartContainsAutoRefills,
	checkIfCartContainsSubscription,
	createCartLineItems,
	createOrderLineItems,
	createTokenAndPostCheckout,
	determineCheckoutUrl,
	determineNextUrl,
	getRecurringCostFromCart,
	getRecurringFrequencyFromCart,
	postCheckout,
	setBodyBusy,
	stripeTokenSave,
}
